import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Typography, Space, Button, Alert, Flex, theme } from 'antd';
import { useAuth } from '@/contexts/AuthContext';
import {
  consentLgpdServico,
  pendenciaResolvida,
  type ConsentPendente,
  type TuteladoConsentPendente,
} from '@/servicos/consent-lgpd.servico';
import { getErrorMessage } from '@/servicos/api';
import { useToast } from '@/hooks/useToast';

const { Paragraph, Text } = Typography;

/**
 * Em que pé está o termo de quem entrou.
 *
 * "verificando" não mostra nada, como antes: a consulta é rápida e a web não
 * tem o que proteger offline. Já a falha não pode sumir sem aviso, que era o
 * que acontecia (o erro ia para um estado que só aparecia dentro do modal de
 * pendência, e o modal só abria quando a consulta dava certo): quem falhava
 * seguia usando o sistema sem o termo. Agora vira "erro", com tentar de novo e
 * sair, a mesma saída do app: nem libera sem saber, nem prende sem saída.
 */
type EstadoTermo =
  | { fase: 'verificando' }
  | { fase: 'liberado' }
  | { fase: 'pendente'; pendencia: ConsentPendente }
  | { fase: 'erro'; mensagem: string };

/** Estado com o dono: a resposta de uma pessoa nunca vale para a próxima. */
interface EstadoComDono {
  pessoaId: string;
  estado: EstadoTermo;
}

type Acao = 'aceitar' | 'recusar' | 'sair' | 'verificar' | `tutelado:${string}`;

function estadoDaPendencia(p: ConsentPendente): EstadoTermo {
  return pendenciaResolvida(p) ? { fase: 'liberado' } : { fase: 'pendente', pendencia: p };
}

export function ConsentLgpdGate() {
  const { usuario, isCarregando, logout } = useAuth();
  const toast = useToast();
  const { token } = theme.useToken();

  // A consulta só faz sentido depois da troca obrigatória de senha.
  const pessoaId =
    usuario && !isCarregando && !usuario.requer_troca_senha ? usuario.pessoa_id : null;

  const [interno, setInterno] = useState<EstadoComDono | null>(null);
  const [acao, setAcao] = useState<Acao | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  /** "Verificar de novo" voltou e o responsável do menor ainda não aceitou. */
  const [aindaSemAceite, setAindaSemAceite] = useState(false);

  // Cada consulta leva o número da sua rodada; a resposta de uma rodada velha
  // (troca de conta, "tentar de novo" por cima de outra) é descartada.
  const rodada = useRef(0);
  // Muda a cada saída da conta. O gate fica montado no App, fora do router, e
  // sobrevive ao login seguinte: sem isto, o erro de um aceite que caiu junto
  // com a sessão (401 com refresh falho encerra a sessão antes de rejeitar)
  // era gravado depois da limpeza e abria o termo da próxima pessoa com ele.
  const sessao = useRef(0);

  const consultar = useCallback(async (dono: string): Promise<EstadoTermo | null> => {
    const minha = ++rodada.current;
    let estado: EstadoTermo;
    try {
      estado = estadoDaPendencia(await consentLgpdServico.pendente());
    } catch (err) {
      estado = { fase: 'erro', mensagem: getErrorMessage(err) };
    }
    if (minha !== rodada.current) return null;
    setInterno({ pessoaId: dono, estado });
    return estado;
  }, []);

  useEffect(() => {
    if (pessoaId) void consultar(pessoaId);
  }, [pessoaId, consultar]);

  // Ao sair (por aqui, pelo menu, por outra aba ou por sessão expirada), o que
  // se sabia do termo vai junto. Sem isso, o modal de quem recusou continuava
  // aberto por cima da tela de login, e quem entrasse de novo na mesma conta
  // via por um instante a resposta velha. O /me que falha na abertura da web
  // limpa a sessão sem o evento, mas não deixa nada para trás: até ele
  // responder, isCarregando segura o pessoaId e o gate não consultou nada.
  useEffect(() => {
    const aoEncerrar = () => {
      rodada.current += 1;
      sessao.current += 1;
      setInterno(null);
      setErroAcao(null);
      setAindaSemAceite(false);
    };
    window.addEventListener('sessao:encerrada', aoEncerrar);
    return () => window.removeEventListener('sessao:encerrada', aoEncerrar);
  }, []);

  /** Tira da pendência o que o servidor acabou de gravar. */
  const baixarDaPendencia = useCallback(
    (dono: string, mudanca: (p: ConsentPendente) => ConsentPendente) => {
      setInterno((atual) => {
        if (!atual || atual.pessoaId !== dono || atual.estado.fase !== 'pendente') return atual;
        return { pessoaId: dono, estado: estadoDaPendencia(mudanca(atual.estado.pendencia)) };
      });
    },
    []
  );

  const estado: EstadoTermo | null = !pessoaId
    ? null
    : interno?.pessoaId === pessoaId
      ? interno.estado
      : { fase: 'verificando' };

  async function executar(qual: Acao, fn: () => Promise<void>) {
    if (acao) return;
    const minhaSessao = sessao.current;
    setErroAcao(null);
    setAcao(qual);
    try {
      await fn();
    } catch (err) {
      if (minhaSessao === sessao.current) setErroAcao(getErrorMessage(err));
    } finally {
      setAcao(null);
    }
  }

  // Sair é ação local: a falha de rede no /logout não prende ninguém aqui (o
  // serviço limpa a sessão no finally de qualquer jeito).
  const sair = () => void executar('sair', () => logout().catch(() => undefined));

  if (!pessoaId || !estado || estado.fase === 'verificando' || estado.fase === 'liberado') {
    return null;
  }

  if (estado.fase === 'erro') {
    return (
      <Modal
        open
        title="Não foi possível verificar o termo"
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={
          <Space>
            <Button onClick={sair} loading={acao === 'sair'} disabled={!!acao && acao !== 'sair'}>
              Sair
            </Button>
            <Button
              type="primary"
              onClick={() =>
                void executar('verificar', async () => {
                  await consultar(pessoaId);
                })
              }
              loading={acao === 'verificar'}
              disabled={!!acao && acao !== 'verificar'}
            >
              Tentar de novo
            </Button>
          </Space>
        }
      >
        <Paragraph>
          Antes de continuar, o Palmer confere se você já aceitou o termo de consentimento de uso
          de dados (LGPD).
        </Paragraph>
        <Alert type="error" showIcon message={estado.mensagem} />
      </Modal>
    );
  }

  const dono = pessoaId;
  const { pendencia } = estado;
  const versao = pendencia.versao_atual;
  const tutelados = pendencia.tutelados_pendentes;
  const aceiteProprio = pendencia.precisa_aceitar && pendencia.pode_autoconsentir;
  // Menor de idade: o aceite dele é do responsável, e aqui só cabe esperar.
  const aguardaResponsavel = pendencia.precisa_aceitar && !pendencia.pode_autoconsentir;
  const podeAceitar = aceiteProprio || tutelados.length > 0;
  // O botão do rodapé aceita tudo de uma vez; o de cada aluno só aparece
  // quando há mais de um aceite a dar, para o responsável poder ir um a um.
  const aceitesPendentes = tutelados.length + (aceiteProprio ? 1 : 0);

  /**
   * Um POST por aceite, em série. O que já foi gravado sai da pendência na
   * hora: se o terceiro falhar, o "Aceitar" de novo manda só o que faltou.
   */
  async function aceitarTudo() {
    if (aceiteProprio) {
      await consentLgpdServico.registrar(versao, true);
      baixarDaPendencia(dono, (p) => ({ ...p, precisa_aceitar: false }));
    }
    for (const t of tutelados) {
      await consentLgpdServico.registrarParaTutelado(t.aluno_pessoa_id, versao, true);
      baixarDaPendencia(dono, (p) => semTutelado(p, t));
    }
    toast.success('Consentimento registrado');
  }

  async function aceitarPorTutelado(t: TuteladoConsentPendente) {
    await consentLgpdServico.registrarParaTutelado(t.aluno_pessoa_id, versao, true);
    baixarDaPendencia(dono, (p) => semTutelado(p, t));
    toast.success(`Consentimento registrado em nome de ${t.aluno_nome}`);
  }

  // Como antes: a recusa vira registro no histórico e a pessoa sai. Só quem
  // tem aceite próprio a dar registra (o menor recebe 403, e a recusa em nome
  // dos filhos não muda nada para eles: continuam pendentes). A falha do
  // registro não segura ninguém dentro, porque sair da conta é ação local.
  async function recusar() {
    if (aceiteProprio) {
      await consentLgpdServico.registrar(versao, false).catch(() => undefined);
    }
    await logout().catch(() => undefined);
  }

  // Sem este retorno, o clique em "Verificar de novo" que não muda nada parece
  // não ter feito nada: o modal volta idêntico depois do carregamento.
  async function verificarDeNovo() {
    setAindaSemAceite(false);
    const novo = await consultar(dono);
    setAindaSemAceite(novo?.fase === 'pendente');
  }

  const mensagemErro =
    erroAcao ??
    (aguardaResponsavel && aindaSemAceite ? 'O termo ainda não foi aceito pelo seu responsável.' : null);

  return (
    <Modal
      open
      title="Termo de Consentimento LGPD"
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={720}
      footer={
        podeAceitar ? (
          <Space>
            <Button
              onClick={() => void executar('recusar', recusar)}
              loading={acao === 'recusar'}
              disabled={!!acao && acao !== 'recusar'}
            >
              Recusar e sair
            </Button>
            <Button
              type="primary"
              onClick={() => void executar('aceitar', aceitarTudo)}
              loading={acao === 'aceitar'}
              disabled={!!acao && acao !== 'aceitar'}
            >
              Aceitar e continuar
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={sair} loading={acao === 'sair'} disabled={!!acao && acao !== 'sair'}>
              Sair
            </Button>
            <Button
              type="primary"
              onClick={() => void executar('verificar', verificarDeNovo)}
              loading={acao === 'verificar'}
              disabled={!!acao && acao !== 'verificar'}
            >
              Verificar de novo
            </Button>
          </Space>
        )
      }
    >
      {aguardaResponsavel && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Menor de idade não pode aceitar o termo por conta própria"
          description="Peça ao seu responsável legal para entrar no Palmer, pela web ou pelo app, e aceitar o termo em seu nome. Depois, clique em Verificar de novo."
        />
      )}
      <Text type="secondary">Versão {versao}</Text>
      <div
        style={{
          maxHeight: 360,
          overflowY: 'auto',
          border: `1px solid ${token.colorBorder}`,
          borderRadius: token.borderRadiusLG,
          padding: 16,
          marginTop: 12,
          background: token.colorFillAlter,
        }}
      >
        {pendencia.texto_termo.split(/\n\n+/).map((paragrafo, i) => (
          <Paragraph key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            {paragrafo}
          </Paragraph>
        ))}
      </div>
      {tutelados.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Paragraph strong style={{ marginBottom: 8 }}>
            {aceiteProprio
              ? 'Ao aceitar e continuar, você consente também em nome dos alunos menores de idade sob sua responsabilidade:'
              : 'Falta o seu aceite em nome dos alunos menores de idade sob sua responsabilidade:'}
          </Paragraph>
          {tutelados.map((t) => {
            const chave = `tutelado:${t.aluno_pessoa_id}` as const;
            return (
              <Flex
                key={t.aluno_pessoa_id}
                align="center"
                justify="space-between"
                gap={12}
                wrap
                style={{ padding: '8px 0', borderTop: `1px solid ${token.colorBorderSecondary}` }}
              >
                <Text strong>{t.aluno_nome}</Text>
                {aceitesPendentes > 1 && (
                  <Button
                    size="small"
                    onClick={() => void executar(chave, () => aceitarPorTutelado(t))}
                    loading={acao === chave}
                    disabled={!!acao && acao !== chave}
                  >
                    Aceitar só por este aluno
                  </Button>
                )}
              </Flex>
            );
          })}
        </div>
      )}
      {mensagemErro && (
        <Alert type="error" style={{ marginTop: 12 }} message={mensagemErro} showIcon />
      )}
    </Modal>
  );
}

function semTutelado(p: ConsentPendente, t: TuteladoConsentPendente): ConsentPendente {
  return {
    ...p,
    tutelados_pendentes: p.tutelados_pendentes.filter(
      (x) => x.aluno_pessoa_id !== t.aluno_pessoa_id
    ),
  };
}
